/**
 * The catalog's navigation model.
 *
 * `slug` is the URL segment (`/design-system/<slug>`) — deep-linkable, so a
 * single component page can be handed to QA or a stakeholder. Every entry has its
 * own page under `pages/<slug>/`; `catalog.entries.spec.ts` enforces that in both
 * directions, so a new entry without a route (or a route without an entry) fails
 * rather than 404ing quietly.
 */
export interface CatalogEntry {
  readonly slug: string;
  readonly label: string;
  /** Sidebar icon — a full Font Awesome class list. */
  readonly icon: string;
}

export interface CatalogGroup {
  readonly title: string;
  readonly entries: readonly CatalogEntry[];
}

export const CATALOG_GROUPS: readonly CatalogGroup[] = [
  {
    title: 'Overview',
    // Slug is `overview`, not `coverage`: the root .gitignore ignores
    // `coverage/` unanchored, which would silently untrack the page directory.
    entries: [
      { slug: 'overview', label: 'Coverage', icon: 'fa-regular fa-chart-simple' },
    ],
  },
  {
    title: 'Tokens',
    entries: [
      { slug: 'colors', label: 'Farben', icon: 'fa-regular fa-palette' },
      {
        slug: 'action-matrix',
        label: 'Aktionsmatrix',
        icon: 'fa-regular fa-table-cells',
      },
      { slug: 'spacing', label: 'Abstände', icon: 'fa-regular fa-ruler' },
      { slug: 'typography', label: 'Typografie', icon: 'fa-regular fa-font' },
      { slug: 'elevation', label: 'Tiefe', icon: 'fa-regular fa-layer-group' },
    ],
  },
  {
    title: 'Components',
    entries: [
      { slug: 'progress-steps', label: 'Progress Steps', icon: 'fa-regular fa-list-ol' },
      { slug: 'process-path', label: 'Process Path', icon: 'fa-regular fa-diagram-project' },
      { slug: 'accordion', label: 'Accordion', icon: 'fa-regular fa-square-caret-down' },
      { slug: 'avatar', label: 'Avatar', icon: 'fa-regular fa-circle-user' },
      { slug: 'avatar-group', label: 'Avatar Group', icon: 'fa-regular fa-user-group' },
      { slug: 'badge', label: 'Badge', icon: 'fa-regular fa-badge' },
      { slug: 'button', label: 'Button', icon: 'fa-regular fa-hand-pointer' },
      { slug: 'button-group', label: 'Button Group', icon: 'fa-regular fa-object-group' },
      { slug: 'card', label: 'Card', icon: 'fa-regular fa-rectangle-list' },
      { slug: 'cell-grid', label: 'Cell Grid', icon: 'fa-regular fa-table-cells-large' },
      { slug: 'chat-bubble', label: 'Chat Bubble', icon: 'fa-regular fa-comment' },
      { slug: 'checkbox', label: 'Checkbox', icon: 'fa-regular fa-square-check' },
      { slug: 'chip', label: 'Chip', icon: 'fa-regular fa-tags' },
      { slug: 'context-menu', label: 'Context Menu', icon: 'fa-regular fa-bars' },
      { slug: 'dot', label: 'Dot', icon: 'fa-regular fa-circle-small' },
      { slug: 'empty-state', label: 'Empty State', icon: 'fa-regular fa-inbox' },
      { slug: 'field-row', label: 'Field Row', icon: 'fa-regular fa-table-list' },
      { slug: 'form-group', label: 'Form Group', icon: 'fa-regular fa-list-check' },
      { slug: 'icon', label: 'Icon', icon: 'fa-regular fa-icons' },
      { slug: 'kbd', label: 'Kbd', icon: 'fa-regular fa-keyboard' },
      { slug: 'kennzeichen', label: 'Kennzeichen', icon: 'fa-regular fa-car' },
      { slug: 'kpi', label: 'KPI', icon: 'fa-regular fa-gauge-high' },
      { slug: 'item', label: 'Item', icon: 'fa-regular fa-list' },
      {
        slug: 'loading-spinner',
        label: 'Loading Spinner',
        icon: 'fa-regular fa-spinner',
      },
      { slug: 'module-item', label: 'Module Item', icon: 'fa-regular fa-cube' },
      { slug: 'notification', label: 'Notification', icon: 'fa-regular fa-bell' },
      { slug: 'number-badge', label: 'Number Badge', icon: 'fa-regular fa-hashtag' },
      { slug: 'overlay', label: 'Overlay', icon: 'fa-regular fa-clone' },
      {
        slug: 'panel-header',
        label: 'Panel Header',
        icon: 'fa-regular fa-window-maximize',
      },
      {
        slug: 'prev-curr-next-navigation',
        label: 'Prev / Curr / Next Navigation',
        icon: 'fa-regular fa-arrow-right-arrow-left',
      },
      {
        slug: 'progress-bars',
        label: 'Progress Bars',
        icon: 'fa-regular fa-bars-progress',
      },
      {
        slug: 'quantity-input',
        label: 'Quantity Input',
        icon: 'fa-regular fa-plus-minus',
      },
      { slug: 'select', label: 'Select', icon: 'fa-regular fa-caret-down' },
      { slug: 'separator', label: 'Separator', icon: 'fa-regular fa-grip-lines' },
      { slug: 'skeleton', label: 'Skeleton', icon: 'fa-regular fa-ellipsis' },
      { slug: 'slider', label: 'Slider', icon: 'fa-regular fa-sliders' },
      { slug: 'split-button', label: 'Split Button', icon: 'fa-regular fa-split' },
      { slug: 'switch', label: 'Switch', icon: 'fa-regular fa-toggle-on' },
      { slug: 'tabs', label: 'Tabs', icon: 'fa-regular fa-table-columns' },
      { slug: 'timeline', label: 'Timeline', icon: 'fa-regular fa-timeline' },
      { slug: 'toggle', label: 'Toggle', icon: 'fa-regular fa-bold' },
    ],
  },
  {
    // Feature-level assemblies, not multi-element components: 'composite' already
    // means Card or Radio Group here (see ComponentPartDocs).
    title: 'Patterns',
    entries: [
      { slug: 'page-filter', label: 'Page Filter', icon: 'fa-regular fa-filter' },
    ],
  },
  {
    title: 'Utilities',
    entries: [
      { slug: 'responsive-wizard', label: 'Responsive Wizard', icon: 'fa-regular fa-shoe-prints' },
    ],
  },
  {
    title: 'Inputs',
    entries: [
      { slug: 'label', label: 'Label', icon: 'fa-regular fa-tag' },
      { slug: 'color-input', label: 'Color Input', icon: 'fa-regular fa-eye-dropper' },
      { slug: 'input', label: 'Input', icon: 'fa-regular fa-input-text' },
      {
        slug: 'number-input',
        label: 'Number Input',
        icon: 'fa-regular fa-input-numeric',
      },
      { slug: 'password-input', label: 'Password Input', icon: 'fa-regular fa-key' },
      {
        slug: 'multiline-input',
        label: 'Multiline Input',
        icon: 'fa-regular fa-align-left',
      },
      { slug: 'date-input', label: 'Date Input', icon: 'fa-regular fa-calendar-day' },
      {
        slug: 'date-time-input',
        label: 'Date Time Input',
        icon: 'fa-regular fa-calendar-clock',
      },
      { slug: 'time-input', label: 'Time Input', icon: 'fa-regular fa-clock' },
      { slug: 'date-picker', label: 'Date Picker', icon: 'fa-regular fa-calendar' },
      {
        slug: 'date-range-picker',
        label: 'Date Range Picker',
        icon: 'fa-regular fa-calendar-week',
      },
      {
        slug: 'date-range-dropdown',
        label: 'Date Range Dropdown',
        icon: 'fa-regular fa-calendar-arrow-down',
      },
      {
        slug: 'date-range-field',
        label: 'Date Range Field',
        icon: 'fa-regular fa-calendar-pen',
      },
      {
        slug: 'date-range-input',
        label: 'Date Range Input',
        icon: 'fa-regular fa-calendar-range',
      },
      { slug: 'radio-group', label: 'Radio Group', icon: 'fa-regular fa-circle-dot' },
    ],
  },
];

export const CATALOG_ENTRIES: readonly CatalogEntry[] = CATALOG_GROUPS.flatMap(
  (group) => group.entries
);

export const findCatalogEntry = (slug: string): CatalogEntry | undefined =>
  CATALOG_ENTRIES.find((entry) => entry.slug === slug);
